import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import SurveyMap from '../components/SurveyMap';
import GisRibbon from '../components/GisRibbon';
import { useTheme } from '../contexts/ThemeContext';
import { extraLayerIds, removeLayerParam } from '../lib/map-url';
import { CRS_OPTIONS } from '../lib/coordinates';
import { useMapPanels } from '../hooks/useMapPanels';
import { useMapData } from '../hooks/useMapData';
import { useMapLayers } from '../hooks/useMapLayers';
import { useMapTools } from '../hooks/useMapTools';
import { useMapSelection } from '../hooks/useMapSelection';
import { useDrawingTools } from '../hooks/useDrawingTools';
import { useToolDrawExclusive } from '../hooks/useToolDrawExclusive';
import { useImportExport } from '../hooks/useImportExport';
import { useMapShortcuts } from '../hooks/useMapShortcuts';
import { useDatasetRawLayers } from '../hooks/useDatasetRawLayers';
import { useBoreholeLayers } from '../hooks/useBoreholeLayers';
import { useRasterLayers } from '../hooks/useRasterLayers';
import { useProjectTerrain } from '../hooks/useTerrainSettings';
import { useVectorTileLayers } from '../hooks/useVectorTileLayers';
import { useProjectDatasetLayers } from '../hooks/useProjectDatasetLayers';
import DatasetGeorefBanner from '../components/map/DatasetGeorefBanner';
import RasterLayerControls, { rasterControlsVisible } from '../components/map/RasterLayerControls';
import VectorTileLayers, { VectorTileStatus } from '../components/map/VectorTileLayers';
import AgentPanel from '../components/map/AgentPanel';
import WorkflowPanel from '../components/map/WorkflowPanel';
import Viewer3DPanel from '../components/map/Viewer3DPanel';
import { workflowDatasetId } from '../lib/workflow-steps';
import LayerTree from '../components/map/LayerTree';
import { useLayerTree } from '../hooks/useLayerTree';
import { useDatasetMenu } from '../hooks/useDatasetMenu';
import { useMapSession } from '../hooks/useMapSession';
import SaveLayersDialog from '../components/map/SaveLayersDialog';
import MapDataLayers from '../components/map/MapDataLayers';
import ViewSyncLayers from '../components/map/ViewSyncLayers';
import NearestLayers from '../components/map/NearestLayers';
import NearestPanel from '../components/map/NearestPanel';
import { useNearestTool } from '../hooks/useNearestTool';
import { useCompareView } from '../hooks/useCompareView';
import ReadingHighlightLayer from '../components/pseudosection/ReadingHighlightLayer';
import { useViewSyncMap } from '../hooks/useViewSyncMap';
import MapOverlays from '../components/map/MapOverlays';
import { sidePanelContent } from '../components/map/MapSidePanels';
import { bottomPanelContent } from '../components/map/MapBottomPanels';
import { useTaskConsole } from '../components/map/TaskConsoleHost';
import DockLayout from '../components/map/DockLayout';
import { useDockLayout } from '../hooks/useDockLayout';
import { openPanelIds, setPanelOpen } from '../lib/panel-registry';
import { useDemoMode, isDemoEnabled, setDemoContextProject } from '../hooks/useDemoMode';
import { resolveRibbonAnalysis, heatmapTargets, heatmapActive, toggleHeatmapStyles, isPointLayer } from '../lib/ribbon-analysis';
import { dockOf } from '../lib/dock-layout';
import { DEFAULT_STYLE } from '../lib/layer-style';
import MapStatusBar from '../components/map/MapStatusBar';
import FoundPlaceMarker from '../components/map/FoundPlaceMarker';
import { flyToResult } from '../lib/geocode';
import MapDialogs from '../components/map/MapDialogs';
import { useOptionalToast } from '../components/Toast';
import { exportMapAsPng } from '../lib/export';
import { buildPopupInfo } from '../components/map/mapPopups';
import { viewer3dPath } from '../lib/map-session';

// MapView is the composition root: state lives in the hooks under hooks/use*.js,
// the JSX lives in components/map/*, and DockLayout places the panels (lib/panel-registry.js).
// See docs/BUILDER_GUIDE.md ("MapView structure").
const DEFAULT_CENTER = { lat: 41.99, lon: -70.15 };

function MapView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { C } = useTheme();
  const { showToast } = useOptionalToast();
  const mapRefHolder = useRef(null);
  const [foundPlace, setFoundPlace] = useState(null); // pin from Map tab > Find address
  const onFindAddress = (r) => { setFoundPlace(r); flyToResult(mapRefHolder.current, r, { duration: 600 }) /* holder.current is SurveyMap's own ref */; };

  const ui = useMapPanels();
  const dock = useDockLayout();
  const taskConsole = useTaskConsole();
  const [demoMode] = useDemoMode();
  const data = useMapData({ searchParams, mapRefHolder, demoMode });
  const demoEnabled = isDemoEnabled(demoMode, data.currentProject);
  // The demo toast says when sample data will not show (a non-Cape-Cod project is open).
  const demoContextProject = data.currentProject;
  useEffect(() => { setDemoContextProject(demoContextProject || null); return () => setDemoContextProject(null); }, [demoContextProject]);
  const lyr = useMapLayers({ mapRefHolder, usgsData: data.usgsData, projectId: data.currentProject?.id, demoEnabled });
  const datasetRaw = useDatasetRawLayers({ searchParams, setResultLayers: lyr.setResultLayers, mapRefHolder });
  useBoreholeLayers({ searchParams, setResultLayers: lyr.setResultLayers, mapRefHolder });
  const pds = useProjectDatasetLayers({ projectId: searchParams.get('project'), datasetId: searchParams.get('dataset'), layerIds: extraLayerIds(searchParams), setImportedLayers: lyr.setImportedLayers, setResultLayers: lyr.setResultLayers, mapRefHolder, overlayOpen: !!(ui.showReportBuilder || ui.showModelBuilder || ui.showGeoreferencing || ui.showMapLayout) });
  const nearest = useNearestTool({ tool: ui.tool, importedLayers: lyr.importedLayers, resultLayers: lyr.resultLayers });
  const raster = useRasterLayers({ searchParams, mapRefHolder });
  useProjectTerrain(data.currentProject?.id); // project DEM for sections, profiles and 3D (docs/DEM_TERRAIN.md)
  const vt = useVectorTileLayers({ searchParams, mapRefHolder }); // large datasets (docs/VECTOR_TILES.md)
  const tools = useMapTools(ui.tool, { onNearestClick: nearest.search });
  const drawing = useDrawingTools({ setResultLayers: lyr.setResultLayers });
  useToolDrawExclusive({ tool: ui.tool, setTool: ui.setTool, activeDrawMode: drawing.activeDrawMode, setDrawMode: drawing.setDrawMode });
  const selection = useMapSelection({
    tool: ui.tool, activeDrawMode: drawing.activeDrawMode,
    layers: lyr.layers, importedLayers: lyr.importedLayers, resultLayers: lyr.resultLayers, onToolClick: tools.handleToolClick,
  });
  const viewSync = useViewSyncMap({
    mapRefHolder, mouseCoord: selection.mouseCoord, viewport: data.viewport,
    inversionModels: ui.inversionModels, showSectionView: ui.showSectionView, sectionModelId: ui.sectionModelId,
  });
  const io = useImportExport({
    mapRefHolder, viewport: data.viewport, layers: lyr.layers,
    importedLayers: lyr.importedLayers, resultLayers: lyr.resultLayers, usgsData: data.usgsData, activeLayerKey: lyr.activeLayerKey,
    setImportedLayers: lyr.setImportedLayers, setProjectCenter: data.setProjectCenter,
  });
  useMapShortcuts({
    fitToData: lyr.fitToData, setDrawMode: drawing.setDrawMode, undoLastDrawn: drawing.undoLastDrawn,
    setTool: ui.setTool, setViewMode: ui.setViewMode,
    resetMeasure: tools.resetMeasure, clearPopups: selection.clearPopups,
  });

  // Properties panel (Wave 5.3): `openProperties(layerId)` opens it on a layer. The layer-tree
  // panel (5.2) calls it from its context menu; the legacy Style buttons set the same state.
  const { setShowStyleEditor } = ui;
  const { setStyleTargetLayer } = lyr;
  const openProperties = useCallback((layerId) => {
    if (layerId) setStyleTargetLayer(layerId);
    setShowStyleEditor(true);
  }, [setStyleTargetLayer, setShowStyleEditor]);
  // Layer tree: entries, draw order (synced to MapLibre) and handlers. Pass `onOpenProperties`
  // (layerId) to route "Properties..." to the properties panel; without it the Style editor opens.
  const layerTree = useLayerTree({ lyr, io, raster, vt, pds, ui, usgsData: data.usgsData, mapRefHolder, onOpenProperties: openProperties,
    // Removing a layer that came from ?layers= also drops its dataset from the URL, so it stays gone on reload.
    onRemoveDataset: (id) => { if (extraLayerIds(searchParams).includes(id)) setSearchParams(removeLayerParam(searchParams, id), { replace: true }); } });

  // Right-click Dataset menu (rename, edit location, rotate, topography, export...) on layer rows and on the map.
  // File tab: auto-saved session (restores on /map without params), New map, Save layers, Recent list.
  const session = useMapSession({
    searchParams, setSearchParams, importedLayers: lyr.importedLayers, setImportedLayers: lyr.setImportedLayers,
    resultLayers: lyr.resultLayers, setResultLayers: lyr.setResultLayers, layerStyles: lyr.layerStyles, setLayerStyles: lyr.setLayerStyles,
    recentName: searchParams.get('dataset') ? data.datasetContext?.datasetName : data.currentProject?.name, showToast,
  });
  const [saveLayersOpen, setSaveLayersOpen] = useState(false);
  const viewingLayers = layerTree.entries.filter((e) => e.group === 'viewing' && e.geojson?.features);
  const saveDisabledWhy = !data.currentProject?.id ? 'Open a project to save layers' : !viewingLayers.length ? 'Nothing to save: no layers in Viewing (not saved)' : null;

  const dsMenu = useDatasetMenu({ lyr, raw: datasetRaw, ui, entries: layerTree.entries, mapRefHolder, navigate, searchParams, setSearchParams });

  // Everything the presentational components under components/map/* read.
  const all = { demoEnabled, ...ui, ...data, ...lyr, ...tools, ...drawing, ...selection, ...io, mapRefHolder, navigate,
    rasterLayers: raster.rasterLayers, raster, vt, projectDatasetCount: pds.datasets.length,
    // A raw survey on the map, placed or not: the pseudosection plots measured values by
    // chainage and pseudo-depth, so it needs no georeference.
    rawDatasetId: ['ready', 'needs-georef', 'vector'].includes(datasetRaw.status) ? datasetRaw.datasetId : null };
  const { projectCenter, restoredView } = all;
  const urlDataset = !!searchParams.get('dataset');
  all.openProperties = openProperties;
  all.onOpenProperties = openProperties;
  // Stored inversion models (V.12): imports are stored with ?project=, linked to ?dataset=,
  // and ?model=<id> opens the Inversion panel on that model.
  all.inversionLinks = { projectId: searchParams.get('project'), datasetId: searchParams.get('dataset'), modelId: searchParams.get('model') };
  const linkedModelId = all.inversionLinks.modelId;
  const { setShowInversionPanel } = ui;
  useEffect(() => { if (linkedModelId) setShowInversionPanel(true); }, [linkedModelId, setShowInversionPanel]);
  const compare = useCompareView({ mapRefHolder, data: all, vt }); // split/swipe comparison (docs/BUILDER_GUIDE.md)

  // Clicking a feature makes its layer the active one (only when it is a layer the panel styles).
  const clickedLayerId = selection.chartSelection?.layerId;
  useEffect(() => { if (clickedLayerId) setStyleTargetLayer(clickedLayerId); }, [selection.chartSelection, clickedLayerId, setStyleTargetLayer]);
  const vtPopupKey = vt.popup ? `vt-${vt.datasetId}-${vt.popup.kind}` : null;
  useEffect(() => { if (vtPopupKey) setStyleTargetLayer(vtPopupKey); }, [vt.popup, vtPopupKey, setStyleTargetLayer]);

  // Panel registry compat layer: the existing useMapPanels booleans (plus the nearest tool,
  // the raster controls and the task console) decide which registry panels are open.
  const panelCtx = { ...all, rasterOpen: rasterControlsVisible(raster), taskConsole };
  const openIds = new Set(openPanelIds(panelCtx));
  // Open a registry panel and bring it to the front of its dock (Workflow panel buttons).
  const revealPanel = (id) => { setPanelOpen(panelCtx, id, true); dock.reveal(id); };
  // Workflow "3D (docked)": the docked panel follows ?dataset=, so point it at the Workflow's dataset, then open/reveal it.
  const openViewer3dDocked = (dsId) => {
    if (dsId && searchParams.get('dataset') !== dsId) {
      const next = new URLSearchParams(searchParams);
      next.set('dataset', dsId);
      setSearchParams(next, { replace: true });
    }
    revealPanel('viewer3d');
  };
  const panelContent = {
    layers: <LayerTree {...all} model={{ ...layerTree, datasetActions: dsMenu.actions }} />,
    viewer3d: ui.showViewer3dPanel ? <Viewer3DPanel projectId={searchParams.get('project')} datasetId={searchParams.get('dataset')} modelId={searchParams.get('model')} navigate={navigate} /> : null,
    workflow: <WorkflowPanel {...all} datasetId={workflowDatasetId(layerTree.entries.find((e) => e.key === lyr.activeLayerKey)?.datasetId, searchParams.get('dataset'))} onTopography={dsMenu.actions.topography} revealPanel={revealPanel} onOpenViewer3d={openViewer3dDocked} />,
    agents: <AgentPanel usgsData={data.usgsData} demoEnabled={demoEnabled} />,
    ...sidePanelContent(all),
    ...bottomPanelContent({ ...all, onOpenViewer3d: openViewer3dDocked }),
    taskConsole: taskConsole.element,
    raster: <RasterLayerControls raster={raster} embedded />,
    nearest: <NearestPanel nearest={nearest} mapRefHolder={mapRefHolder} embedded />,
  };

  // ── Ribbon > Analysis / Map > Heatmap handlers ──
  // A docked panel opened from the ribbon comes to the front of its dock even when it was
  // already open behind another tab; a second click on the front tab closes it.
  const toggleDocked = (id, isOpenNow, setOpen) => {
    const where = dockOf(dock.layout, id);
    if (isOpenNow && where && where !== 'float' && (dock.layout.active[where] !== id || dock.layout.collapsed[where])) {
      dock.reveal(id);
      return;
    }
    setOpen(!isOpenNow);
    if (!isOpenNow) dock.reveal(id);
  };
  // Heatmap: the 5.3 heatmap style on the active point layer (or every visible point layer)
  // and back; the legacy soundings heatmap follows it only for the demo data.
  const heatmapLayers = lyr.chartableLayers.map((l) => {
    const usgs = l.id.startsWith('usgs-') ? lyr.layers.find((x) => `usgs-${x.id}` === l.id) : null;
    const own = usgs || lyr.importedLayers.find((x) => x.id === l.id) || lyr.resultLayers.find((x) => x.id === l.id);
    return { id: l.id, geojson: l.geojson, visible: own ? own.visible !== false : true };
  });
  const heatmapOn = ui.showHeatmap || heatmapActive(lyr.layerStyles, heatmapLayers.filter((l) => isPointLayer(l.geojson)).map((l) => l.id));
  const toggleHeatmap = () => {
    const targets = heatmapTargets(heatmapLayers, lyr.styleTargetLayer);
    const { on, updates } = toggleHeatmapStyles(lyr.layerStyles, targets, DEFAULT_STYLE);
    updates.forEach(([id, style]) => lyr.applyLayerStyle(id, style));
    ui.setShowHeatmap(on && demoEnabled);
  };
  const onRibbonAnalysis = (ribbonId) => {
    const r = resolveRibbonAnalysis(ribbonId);
    if (r.action === 'nearest') { ui.setTool('nearest'); return; }
    if (r.action === 'heatmap') { toggleHeatmap(); return; }
    // A new key remounts AnalysisPanel on the tool (or on the bare list for All Tools).
    ui.setAnalysisInit(r.action === 'panel' ? { _k: Date.now(), tool: r.tool, params: r.params } : { _k: Date.now() });
    ui.setShowAnalysisPanel(true);
    dock.reveal('analysis');
  };

  const ribbonElement = (
    <GisRibbon
      // Map tab
      viewMode={ui.viewMode}
      setViewMode={ui.setViewMode}
      showHeatmap={heatmapOn}
      setShowHeatmap={toggleHeatmap}
      tool={ui.tool}
      setTool={ui.setTool}
      coordSys={ui.coordSys}
      setCoordSys={ui.setCoordSys}
      CRS_OPTIONS={CRS_OPTIONS}
      onFindAddress={onFindAddress}
      // File tab
      onNewMap={session.newMap}
      onSaveLayers={() => setSaveLayersOpen(true)}
      saveDisabledWhy={saveDisabledWhy}
      autosave={session.autosave}
      onToggleAutosave={session.setAutosave}
      onRestoreSession={session.restoreLast}
      canRestoreSession={session.canRestore}
      // Insert tab
      onImportFile={() => io.setShowFileImport(true)}
      onAddService={(type) => io.setShowServiceDialog(type || true)}
      onGeoreference={() => ui.setShowGeoreferencing(true)}
      onShowInversion={() => ui.setShowInversionPanel(v => !v)}
      showInversionPanel={ui.showInversionPanel}
      // Edit tab
      showDrawingPanel={drawing.showDrawingPanel}
      onToggleDrawing={() => drawing.setShowDrawingPanel(v => !v)}
      activeDrawMode={drawing.activeDrawMode}
      onDrawMode={(mode) => { drawing.setDrawMode(mode === drawing.activeDrawMode ? null : mode); if (!drawing.showDrawingPanel) drawing.setShowDrawingPanel(true); }}
      // Analysis tab
      onShowAnalysisPanel={onRibbonAnalysis}
      onShowModelBuilder={() => ui.setShowModelBuilder(true)}
      onShowGridCombine={() => toggleDocked('gridCombine', ui.showGridCombine, ui.setShowGridCombine)}
      onShowGridProfile={() => toggleDocked('gridProfile', ui.showGridProfile, ui.setShowGridProfile)}
      showGridProfile={ui.showGridProfile}
      onShowVariogram={() => toggleDocked('variogram', ui.showVariogram, ui.setShowVariogram)}
      onShowSectionView={() => toggleDocked('section', ui.showSectionView, ui.setShowSectionView)}
      showAnalysisPanel={ui.showAnalysisPanel}
      showGridCombine={ui.showGridCombine}
      showVariogram={ui.showVariogram}
      showSectionView={ui.showSectionView}
      rawDatasetId={all.rawDatasetId}
      pseudosectionOpen={!!ui.pseudosection?.datasetId && !ui.pseudosection?.model}
      onTogglePseudosection={() => ui.setPseudosection(ui.pseudosection?.datasetId && !ui.pseudosection?.model ? null : { datasetId: all.rawDatasetId })}
      // View tab
      showLayerPanel={ui.showLayerPanel}
      setShowLayerPanel={ui.setShowLayerPanel}
      showWorkflowPanel={ui.showWorkflowPanel}
      setShowWorkflowPanel={ui.setShowWorkflowPanel}
      showViewer3dPanel={ui.showViewer3dPanel}
      onToggleViewer3d={() => toggleDocked('viewer3d', ui.showViewer3dPanel, ui.setShowViewer3dPanel)}
      showAgentPanel={ui.showAgentPanel}
      setShowAgentPanel={ui.setShowAgentPanel}
      showAttributeTable={ui.showAttributeTable}
      setShowAttributeTable={ui.setShowAttributeTable}
      showChartPanel={ui.showChartPanel}
      setShowChartPanel={ui.setShowChartPanel}
      showStyleEditor={ui.showStyleEditor}
      setShowStyleEditor={ui.setShowStyleEditor}
      navigate={navigate}
      showCompare={compare.active}
      onToggleCompare={compare.toggle}
      dockCollapsed={dock.layout.collapsed}
      onToggleDock={dock.toggleCollapsed}
      onResetLayout={dock.reset}
      // Reports tab
      onShowMapLayout={() => ui.setShowMapLayout(true)}
      onShowReportBuilder={() => { ui.setReportType(null); ui.setShowReportBuilder(true); }}
      onShowProjectReport={(type) => { ui.setReportType(type); ui.setShowReportBuilder(true); }}
      onQuickExport={() => {
        const ok = exportMapAsPng(mapRefHolder.current);
        showToast(ok ? 'Map exported as PNG' : 'Map is not ready to export yet', ok ? 'success' : 'error');
      }}
      // Share tab
      {...io.exportHandlers}
    />
  );

  return (
    <Layout ribbon={ribbonElement}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, minHeight: 0 }}>
          <DockLayout
            layout={dock.layout}
            actions={dock}
            isOpen={(id) => openIds.has(id)}
            onClose={(id) => setPanelOpen(panelCtx, id, false)}
            content={panelContent}
          >
            <SurveyMap
              viewMode={ui.viewMode}
              // `?dataset=`: the dataset loader fits the camera to the data, so the saved view and the
              // project centre must not recentre the map once they arrive (they load asynchronously).
              initialCenter={urlDataset ? DEFAULT_CENTER : (restoredView ? { lat: restoredView.latitude, lon: restoredView.longitude } : (projectCenter || DEFAULT_CENTER))}
              initialZoom={urlDataset ? 10 : (restoredView ? restoredView.zoom : (projectCenter?.zoom || 10))}
              onViewportChange={data.handleViewportChange}
              onMapClick={(evt) => { if (!raster.handleMapClick(evt)) selection.handleMapClick(evt); }}
              onDblClick={tools.handleMapDblClick}
              onMouseMove={selection.handleMouseMove}
              onContextMenu={dsMenu.onMapContextMenu}
              onMapRef={(ref) => { mapRefHolder.current = ref; }}
              onDrawReady={drawing.handleDrawReady}
              cursor={ui.tool === 'measure' || ui.tool === 'nearest' ? 'crosshair' : selection.cursor}
              interactiveLayerIds={lyr.interactiveLayerIds}
              popupInfo={buildPopupInfo({ ...selection, C, navigate, viewer3dHref: viewer3dPath(searchParams.get('project'), { dataset: searchParams.get('dataset'), model: searchParams.get('model'), focus: true }) })}
              onPopupClose={selection.clearPopups}
            >
              <MapDataLayers {...all} />
              <VectorTileLayers vt={vt} />
              <NearestLayers nearest={nearest} />
              <ViewSyncLayers sectionLine={viewSync.sectionLine} hovered={viewSync.hovered} />
              <ReadingHighlightLayer resultLayers={lyr.resultLayers} />
              <FoundPlaceMarker pin={foundPlace} />
            </SurveyMap>
            {compare.element}
            {/* Overlays that stay on the map (not dockable) */}
            <DatasetGeorefBanner raw={datasetRaw} />
            <VectorTileStatus vt={vt} />
            <MapOverlays {...all} />
            {/* Sync camera toggle hidden (TODO P.20): the map and the 3D viewer are never on screen together. */}
          </DockLayout>
        </div>
        <MapStatusBar {...all} demoEnabled={demoEnabled} />
      </div>

      <MapDialogs {...all} />
      <SaveLayersDialog open={saveLayersOpen} onClose={() => setSaveLayersOpen(false)} layers={viewingLayers} project={data.currentProject}
        onSaved={(saved) => layerTree.actions.layersSaved?.(saved)} />
      {dsMenu.element}
      {pds.mappingElement}
      {io.shareElement}
    </Layout>
  );
}

export default MapView;
